import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCreateOrder, useCustomers, useLocations, useCreateCustomer } from '@t90/api-client';
import type { Customer, Location } from '@t90/common/supabase';
import {
  Button,
  Input,
  Textarea,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  ArrowLeft,
  Loader2,
  Save,
  Plus,
} from '@t90/ui';

// Form validation schema
const newOrderSchema = z.object({
  customer_id: z.string().min(1, 'Customer is required'),
  order_date: z.string().min(1, 'Order date is required'),
  due_date: z.string().optional(),
  order_description: z.string().optional(),
  po_number: z.string().optional(),
  priority: z.enum(['Low', 'Normal', 'High', 'Urgent']),
  source: z.enum(['Direct', 'Website', 'Phone', 'Email', 'Referral']).optional(),
  location_id: z.string().optional(),
  notes: z.string().optional(),
});

type NewOrderFormData = z.infer<typeof newOrderSchema>;

// Schema for new customer form
const newCustomerSchema = z.object({
  customer_name: z.string().min(1, 'Customer name is required'),
  contact_name: z.string().optional(),
  contact_email: z.string().email('Invalid email').optional().or(z.literal('')),
  contact_phone: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip_code: z.string().optional(),
  payment_terms: z.string().default('Net 30'),
});

type NewCustomerFormData = z.infer<typeof newCustomerSchema>;

export function NewOrderPage() {
  const navigate = useNavigate();
  const [isNewCustomerDialogOpen, setIsNewCustomerDialogOpen] = useState(false);
  
  const createOrder = useCreateOrder();
  const createCustomer = useCreateCustomer();
  const { data: customers, isLoading: customersLoading } = useCustomers({ active: true });
  const { data: locations, isLoading: locationsLoading } = useLocations({ active: true });

  // Cast the data to proper types
  const customerList = (customers ?? []) as Customer[];
  const locationList = (locations ?? []) as Location[];

  // Order form
  const form = useForm<NewOrderFormData>({
    resolver: zodResolver(newOrderSchema),
    defaultValues: {
      customer_id: '',
      order_date: new Date().toISOString().split('T')[0],
      due_date: '',
      order_description: '',
      po_number: '',
      priority: 'Normal',
      source: undefined,
      location_id: '',
      notes: '',
    },
  });

  // New customer form
  const customerForm = useForm<NewCustomerFormData>({
    resolver: zodResolver(newCustomerSchema),
    defaultValues: {
      customer_name: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      payment_terms: 'Net 30',
    },
  });

  async function onCreateCustomer(data: NewCustomerFormData) {
    try {
      const customerData = {
        customer_name: data.customer_name,
        contact_name: data.contact_name || null,
        contact_email: data.contact_email || null,
        contact_phone: data.contact_phone || null,
        address: data.address || null,
        city: data.city || null,
        state: data.state || null,
        zip_code: data.zip_code || null,
        payment_terms: data.payment_terms,
      };

      const result = await createCustomer.mutateAsync(customerData);
      // Set the newly created customer as selected
      form.setValue('customer_id', result.id);
      // Close the dialog and reset form
      setIsNewCustomerDialogOpen(false);
      customerForm.reset();
    } catch (error) {
      console.error('Failed to create customer:', error);
    }
  }

  async function onSubmit(data: NewOrderFormData) {
    try {
      const orderData = {
        customer_id: data.customer_id,
        order_date: data.order_date,
        due_date: data.due_date || null,
        order_description: data.order_description || null,
        po_number: data.po_number || null,
        priority: data.priority,
        source: data.source || null,
        location_id: data.location_id || null,
        notes: data.notes || null,
      };

      const result = await createOrder.mutateAsync(orderData);
      navigate(`/orders/${result.id}`);
    } catch (error) {
      // Error is handled by the mutation's onError callback
      console.error('Failed to create order:', error);
    }
  }

  const isLoading = customersLoading || locationsLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/orders')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">New Order</h1>
          <p className="text-muted-foreground">
            Create a new customer order
          </p>
        </div>
      </div>

      {/* Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Order Details</CardTitle>
              <CardDescription>
                Enter the basic information for this order
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Customer Selection */}
              <FormField
                control={form.control}
                name="customer_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Customer *</FormLabel>
                    <div className="flex gap-2">
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={isLoading}
                      >
                        <FormControl>
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Select a customer" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {customerList.map((customer) => (
                            <SelectItem key={customer.id} value={customer.id}>
                              {customer.customer_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Dialog open={isNewCustomerDialogOpen} onOpenChange={setIsNewCustomerDialogOpen}>
                        <DialogTrigger asChild>
                          <Button type="button" variant="outline" size="icon">
                            <Plus className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                          <DialogHeader>
                            <DialogTitle>Add New Customer</DialogTitle>
                            <DialogDescription>
                              Create a new customer to add to this order
                            </DialogDescription>
                          </DialogHeader>
                          <form onSubmit={customerForm.handleSubmit(onCreateCustomer)} className="space-y-4">
                            <div className="space-y-4">
                              <div>
                                <label className="text-sm font-medium">Customer Name *</label>
                                <Input
                                  {...customerForm.register('customer_name')}
                                  placeholder="Company or customer name"
                                  className="mt-1.5"
                                />
                                {customerForm.formState.errors.customer_name && (
                                  <p className="text-sm text-destructive mt-1">
                                    {customerForm.formState.errors.customer_name.message}
                                  </p>
                                )}
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm font-medium">Contact Name</label>
                                  <Input
                                    {...customerForm.register('contact_name')}
                                    placeholder="Primary contact"
                                    className="mt-1.5"
                                  />
                                </div>
                                <div>
                                  <label className="text-sm font-medium">Contact Phone</label>
                                  <Input
                                    {...customerForm.register('contact_phone')}
                                    placeholder="(555) 123-4567"
                                    className="mt-1.5"
                                  />
                                </div>
                              </div>
                              
                              <div>
                                <label className="text-sm font-medium">Contact Email</label>
                                <Input
                                  {...customerForm.register('contact_email')}
                                  type="email"
                                  placeholder="email@example.com"
                                  className="mt-1.5"
                                />
                                {customerForm.formState.errors.contact_email && (
                                  <p className="text-sm text-destructive mt-1">
                                    {customerForm.formState.errors.contact_email.message}
                                  </p>
                                )}
                              </div>
                              
                              <div>
                                <label className="text-sm font-medium">Address</label>
                                <Input
                                  {...customerForm.register('address')}
                                  placeholder="Street address"
                                  className="mt-1.5"
                                />
                              </div>
                              
                              <div className="grid grid-cols-3 gap-4">
                                <div>
                                  <label className="text-sm font-medium">City</label>
                                  <Input
                                    {...customerForm.register('city')}
                                    placeholder="City"
                                    className="mt-1.5"
                                  />
                                </div>
                                <div>
                                  <label className="text-sm font-medium">State</label>
                                  <Input
                                    {...customerForm.register('state')}
                                    placeholder="CA"
                                    className="mt-1.5"
                                  />
                                </div>
                                <div>
                                  <label className="text-sm font-medium">ZIP Code</label>
                                  <Input
                                    {...customerForm.register('zip_code')}
                                    placeholder="12345"
                                    className="mt-1.5"
                                  />
                                </div>
                              </div>
                              
                              <div>
                                <label className="text-sm font-medium">Payment Terms</label>
                                <Select
                                  onValueChange={(value) => customerForm.setValue('payment_terms', value)}
                                  defaultValue="Net 30"
                                >
                                  <SelectTrigger className="mt-1.5">
                                    <SelectValue placeholder="Select payment terms" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Net 15">Net 15</SelectItem>
                                    <SelectItem value="Net 30">Net 30</SelectItem>
                                    <SelectItem value="Net 45">Net 45</SelectItem>
                                    <SelectItem value="Net 60">Net 60</SelectItem>
                                    <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <DialogFooter>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  setIsNewCustomerDialogOpen(false);
                                  customerForm.reset();
                                }}
                              >
                                Cancel
                              </Button>
                              <Button type="submit" disabled={createCustomer.isPending}>
                                {createCustomer.isPending ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Creating...
                                  </>
                                ) : (
                                  'Create Customer'
                                )}
                              </Button>
                            </DialogFooter>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Dates Row */}
              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="order_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Order Date *</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="due_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Due Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormDescription>
                        When should this order be completed?
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Order Description */}
              <FormField
                control={form.control}
                name="order_description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Order Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe the order..."
                        className="min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* PO Number & Priority Row */}
              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="po_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>PO Number</FormLabel>
                      <FormControl>
                        <Input placeholder="Customer PO number" {...field} />
                      </FormControl>
                      <FormDescription>
                        Customer's purchase order reference
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="priority"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Priority</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select priority" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Low">Low</SelectItem>
                          <SelectItem value="Normal">Normal</SelectItem>
                          <SelectItem value="High">High</SelectItem>
                          <SelectItem value="Urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Source & Location Row */}
              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="source"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Order Source</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="How was this order received?" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Direct">Direct</SelectItem>
                          <SelectItem value="Website">Website</SelectItem>
                          <SelectItem value="Phone">Phone</SelectItem>
                          <SelectItem value="Email">Email</SelectItem>
                          <SelectItem value="Referral">Referral</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="location_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Location</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        disabled={locationsLoading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Assign to location" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {locationList.map((location) => (
                            <SelectItem key={location.id} value={location.id}>
                              {location.location_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Which location will process this order?
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Notes */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Internal Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Add any internal notes about this order..."
                        className="min-h-[80px]"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      These notes are for internal use only
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
            <CardFooter className="flex justify-between border-t pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/orders')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createOrder.isPending}>
                {createOrder.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Create Order
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
}
